import { useEffect, useRef, useState } from 'react';

export const studioCountdownSeconds = 5;

const isPageActive = () => document.visibilityState === 'visible' && document.hasFocus();

/**
 * Counts down only while the page is visible and focused. Losing focus resets the
 * countdown, so it always restarts in full view. Any pointer or key input cancels
 * it for the rest of the page view.
 *
 * `remaining` is the whole seconds left, or undefined when no countdown is showing.
 */
export function useStudioCountdown(enabled: boolean, onElapsed: () => void) {
  const [active, setActive] = useState(isPageActive);
  const [stopped, setStopped] = useState(false);
  const [left, setLeft] = useState(studioCountdownSeconds);
  const elapsed = useRef(onElapsed);
  // eslint-disable-next-line @silverhand/fp/no-mutation -- The running timer must call the latest callback without restarting.
  elapsed.current = onElapsed;
  const running = enabled && !stopped;

  useEffect(() => {
    if (!running) {
      return;
    }
    const sync = () => {
      setActive(isPageActive());
    };
    const cancel = () => {
      setStopped(true);
    };
    sync();
    window.addEventListener('focus', sync);
    window.addEventListener('blur', sync);
    document.addEventListener('visibilitychange', sync);
    document.addEventListener('pointerdown', cancel, true);
    document.addEventListener('keydown', cancel, true);
    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('blur', sync);
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('pointerdown', cancel, true);
      document.removeEventListener('keydown', cancel, true);
    };
  }, [running]);

  useEffect(() => {
    if (!running || !active) {
      return;
    }
    const deadline = Date.now() + studioCountdownSeconds * 1000;
    const timer = setInterval(() => {
      const seconds = Math.ceil((deadline - Date.now()) / 1000);
      if (seconds > 0) {
        setLeft(seconds);
        return;
      }
      clearInterval(timer);
      setStopped(true);
      elapsed.current();
    }, 200);
    return () => {
      clearInterval(timer);
      setLeft(studioCountdownSeconds);
    };
  }, [running, active]);

  return {
    remaining: running && active ? left : undefined,
    cancel: () => {
      setStopped(true);
    },
  };
}
